import { ActionButton } from "../ActionButton/ActionButton";
import s from "./DropdownButton.module.scss";
import { FC, useState, useEffect, useRef } from "react";

export interface DropdownButtonProps {
  id: string;
  icon?: React.ReactNode;
  label?: string;
  children: React.ReactNode;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  disabled?: boolean;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  className?: string;
   "data-tour"?: string;
}

export const DropdownButton: FC<DropdownButtonProps> = ({
  id,
  icon,
  label,
  children,
  isOpen: externalIsOpen,
  onToggle,
  disabled = false,
  onBlur,
  className,
  ...rest
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Determine if component is controlled or uncontrolled
  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;

  useEffect(() => {
    if (isControlled) {
      setInternalIsOpen(externalIsOpen);
    }
  }, [externalIsOpen, isControlled]);

  const toggle = () => {
    if (disabled) return;
    const newIsOpen = !isOpen;
    if (!isControlled) {
      setInternalIsOpen(newIsOpen);
    }
    if (onToggle) {
      onToggle(newIsOpen);
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!rootRef.current) return;
      if (target && rootRef.current.contains(target)) return;
      if (disabled) return;

      if (!isControlled) {
        setInternalIsOpen(false);
      }
      onToggle?.(false);
    };

    document.addEventListener("mousedown", handleDocumentClick, true);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick, true);
    };
  }, [isOpen, isControlled, disabled, onToggle]);

  // Close when clicking inside iframe (custom event dispatched from iframe document)
  useEffect(() => {
    if (!isOpen) return;

    const handleIframePointerDown = () => {
      if (disabled) return;
      if (!isControlled) {
        setInternalIsOpen(false);
      }
      onToggle?.(false);
    };

    window.addEventListener("iframe-pointerdown", handleIframePointerDown);
    return () => {
      window.removeEventListener("iframe-pointerdown", handleIframePointerDown);
    };
  }, [isOpen, isControlled, disabled, onToggle]);

  return (
    <div
      ref={rootRef}
      className={`${s.dropdown} ${isOpen ? s.active : ""} ${className ?? ""}`}
      {...rest}
    >
      <ActionButton
        id={id}
        icon={icon}
        label={label}
        onClick={toggle}
        isActive={isOpen}
        disabled={disabled}
      />
      {isOpen && !disabled && <div className={s.content}>{children}</div>}
    </div>
  );
};
